import React, { useEffect, useRef, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Bell } from 'lucide-react';
import { UserIcon, DocumentTextIcon, ChatIcon, XCircleIcon } from './common/Icons';
import { useAuth } from '../context/AuthContext';
import Sidebar from './Sidebar';
import MobileScrollableNav from './MobileScrollableNav';
import ResponsiveLogo from './ResponsiveLogo';

const DashboardLayout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const profileMenuRef = useRef(null);

  const handleNotificationsClick = () => {
    if (location.pathname !== '/dashboard') {
      navigate('/dashboard');
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target)) {
        setIsProfileMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleProfileMenuToggle = () => {
    setIsProfileMenuOpen((prev) => !prev);
  };

  const handleProfileMenuNavigate = (path) => {
    navigate(path);
    setIsProfileMenuOpen(false);
  };

  const handleProfileMenuLogout = async () => {
    setIsProfileMenuOpen(false);
    await logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-slate-50 font-sans">
      <Sidebar />

      <div className="md:ml-64 min-h-screen flex flex-col">
        <header className="bg-white border-b border-slate-200 px-6 h-[72px] flex items-center justify-between sticky top-0 z-20">
          {/* Mobile branding */}
          <div className="flex items-center gap-3 md:hidden">
            <ResponsiveLogo alt="CITNC Logo" className="h-8" />
            <h1 className="text-xl font-bold text-slate-800 tracking-tight">CITNC AIO</h1>
          </div>
          {/* Desktop branding — shown only on md+ */}
          <div className="hidden md:flex items-center gap-3">
            <h1 className="text-xl font-bold text-slate-800 tracking-tight">CITNC AIO</h1>
          </div>
          <div className="flex items-center gap-4">
            <button
              className="relative p-2 text-slate-600 hover:bg-slate-100 rounded-full transition-colors"
              type="button"
              onClick={handleNotificationsClick}
              aria-label="Notifications"
            >
              <Bell className="w-5 h-5" />
              <span className="absolute top-1.5 right-2 w-2 h-2 bg-amber-500 rounded-full border border-white"></span>
            </button>
            <div className="relative" ref={profileMenuRef}>
              <button
                className="w-8 h-8 rounded-full bg-slate-200 overflow-hidden border border-slate-300"
                type="button"
                onClick={handleProfileMenuToggle}
                aria-label="Profile menu"
                aria-expanded={isProfileMenuOpen}
                aria-haspopup="menu"
              >
                <img
                  src={`https://ui-avatars.com/api/?name=${encodeURIComponent(user?.name || 'User')}&background=0D8ABC&color=fff`}
                  alt="Profile"
                  className="w-full h-full object-cover"
                />
              </button>

              {isProfileMenuOpen && (
                <div
                  className="absolute right-0 mt-2 w-44 rounded-lg border border-slate-200 bg-white shadow-lg py-1 z-50"
                  role="menu"
                  aria-label="Profile options"
                >
                  <button
                    type="button"
                    className="w-full px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-100 flex items-center gap-3"
                    onClick={() => handleProfileMenuNavigate('/profile')}
                    role="menuitem"
                  >
                    <UserIcon className="w-4 h-4 text-slate-600" />
                    Profile
                  </button>
                  <button
                    type="button"
                    className="w-full px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-100 flex items-center gap-3"
                    onClick={() => handleProfileMenuNavigate('/help')}
                    role="menuitem"
                  >
                    <DocumentTextIcon className="w-4 h-4 text-slate-600" />
                    Help Centre
                  </button>
                  <button
                    type="button"
                    className="w-full px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-100 flex items-center gap-3"
                    onClick={() => handleProfileMenuNavigate('/contact')}
                    role="menuitem"
                  >
                    <ChatIcon className="w-4 h-4 text-slate-600" />
                    Contact Us
                  </button>
                  <button
                    type="button"
                    className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-3"
                    onClick={handleProfileMenuLogout}
                    role="menuitem"
                  >
                    <XCircleIcon className="w-4 h-4 text-red-600" />
                    Logout
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        <main className="flex-1 p-6 pb-24 md:pb-6 max-w-7xl mx-auto w-full">
          <Outlet />
        </main>
      </div>
      <MobileScrollableNav />
    </div>
  );
};

export default DashboardLayout;

